import {LegendStop} from './legend-range.util';

/** Representative band colors on daily deviation tiles (EraDeviationTilesRunner.DeviationToColor). */
export const DEVIATION_BAND = {
  temperature: {cold: '#0000FF', normal: '#00FF00', hot: '#FF0000'},
  fallout: {dry: '#FFFF00', normal: '#00FF00', wet: '#0000FF'},
  humidity: {low: '#FF0000', normal: '#00FF00', high: '#0000FF'}
} as const;

/**
 * Full tile color including intensity within a band (for tests / range logic only).
 * The visible legend uses {@link DEVIATION_BAND} flat colors only.
 */
export function deviationTemperatureColor(deviation: number): string {
  if (deviation > 3) {
    const intensity = Math.min(255, ((deviation - 3) / 10) * 255);
    return rgb(255, 255 - intensity, 255 - intensity);
  }
  if (deviation < -3) {
    const intensity = Math.min(255, ((-deviation - 3) / 10) * 255);
    return rgb(255 - intensity, 255 - intensity, 255);
  }
  const minGreen = 24;
  const ratio = (deviation + 3) / 6;
  const green = Math.max(minGreen, 255 * (1 - Math.abs(ratio - 0.5) * 2));
  return rgb(0, green, 0);
}

export function deviationFalloutColor(deviation: number): string {
  if (deviation > 5) {
    return DEVIATION_BAND.fallout.wet;
  }
  if (deviation < -5) {
    return DEVIATION_BAND.fallout.dry;
  }
  return DEVIATION_BAND.fallout.normal;
}

export function deviationHumidityColor(deviation: number): string {
  if (deviation > 20) {
    return DEVIATION_BAND.humidity.high;
  }
  if (deviation < -20) {
    return DEVIATION_BAND.humidity.low;
  }
  return DEVIATION_BAND.humidity.normal;
}

const DEVIATION_BAND_LABELS = {
  below: 'BELOW_NORMAL',
  normal: 'NORMAL',
  above: 'ABOVE_NORMAL'
} as const;

/** Three band colors only — no gradient between blue, green, and red. */
export function temperatureDeviationLegendStops(): LegendStop[] {
  return [
    {value: DEVIATION_BAND_LABELS.below, color: DEVIATION_BAND.temperature.cold},
    {value: DEVIATION_BAND_LABELS.normal, color: DEVIATION_BAND.temperature.normal},
    {value: DEVIATION_BAND_LABELS.above, color: DEVIATION_BAND.temperature.hot}
  ];
}

export function falloutDeviationLegendStops(): LegendStop[] {
  return [
    {value: DEVIATION_BAND_LABELS.below, color: DEVIATION_BAND.fallout.dry},
    {value: DEVIATION_BAND_LABELS.normal, color: DEVIATION_BAND.fallout.normal},
    {value: DEVIATION_BAND_LABELS.above, color: DEVIATION_BAND.fallout.wet}
  ];
}

export function humidityDeviationLegendStops(): LegendStop[] {
  return [
    {value: DEVIATION_BAND_LABELS.below, color: DEVIATION_BAND.humidity.low},
    {value: DEVIATION_BAND_LABELS.normal, color: DEVIATION_BAND.humidity.normal},
    {value: DEVIATION_BAND_LABELS.above, color: DEVIATION_BAND.humidity.high}
  ];
}

export function genericDeviationLegendStops(): LegendStop[] {
  return [{value: '0', color: '#808080'}];
}

function rgb(r: number, g: number, b: number): string {
  const clamp = (v: number) => Math.round(Math.max(0, Math.min(255, v)));
  return `#${[clamp(r), clamp(g), clamp(b)]
    .map((v) => v.toString(16).padStart(2, '0'))
    .join('')}`;
}
